import React, { useEffect, useState } from 'react';
import { tenantAdminApi } from '@/utils/tenantAdminApi';
import { rbacApi } from '@/modules/admin-rbac/api/rbac.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DrawerShell from '@/components/DrawerShell';
import { UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InviteUserDrawer({ onDone }) {
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '', name: '', password: '', role_key: '', specialization: '',
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [r, s] = await Promise.all([
          rbacApi.loadRoles(true),
          rbacApi.loadSpecialisations(true),
        ]);
        setRoles(r);
        setSpecs(s);
        if (!form.role_key && r[0]) {
          const preferred = r.find((x) => (x.name || '').includes('Resident')) || r[0];
          setForm((f) => ({ ...f, role_key: preferred.role_key }));
        }
      } catch (_) { /* ignore */ }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.email || !form.name || !form.password || !form.role_key) {
      toast.error('Email, name, password and role are required'); return;
    }
    setSaving(true);
    try {
      await tenantAdminApi.invite({
        email: form.email,
        name: form.name,
        password: form.password,
        role_key: form.role_key,
        specialization: form.specialization || undefined,
      });
      toast.success('User created');
      setOpen(false);
      setForm({ email: '', name: '', password: '', role_key: '', specialization: '' });
      onDone();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to invite');
    }
    setSaving(false);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-users-invite-btn">
        <UserPlus className="h-4 w-4 mr-2" /> Add user
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Team"
        title="Add user"
        subtitle="Assign a hospital role and specialisation"
        accent="#1d4ed8"
        icon={<UserPlus className="h-5 w-5" />}
        width="md"
        testId="ta-invite-drawer"
        footer={(
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-invite-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create user
            </Button>
          </>
        )}
      >
        <div className="p-5 space-y-3">
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ta-invite-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ta-invite-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Hospital role *</Label>
            <select
              className="w-full h-10 border rounded-md px-3 text-sm"
              value={form.role_key}
              onChange={(e) => setForm({ ...form, role_key: e.target.value })}
              data-testid="ta-invite-role"
            >
              <option value="">— Select —</option>
              {roles.map((r) => (
                <option key={r.role_key} value={r.role_key}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Specialisation</Label>
            <select
              className="w-full h-10 border rounded-md px-3 text-sm"
              value={form.specialization}
              onChange={(e) => setForm({ ...form, specialization: e.target.value })}
              data-testid="ta-invite-spec"
            >
              <option value="">— Select —</option>
              {specs.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Temporary password *</Label>
            <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ta-invite-pass" />
          </div>
        </div>
      </DrawerShell>
    </>
  );
}
